import DrShotAnalysisReportStyle from './DrShotAnalysisReport.module.scss';
import * as React from 'react';
import { Injector } from 'Core';
import { UnitPresentation, UnitSystem } from 'MeasurementsCalculations';
import { IReportDataService, IReportDataConversionService } from 'Services';
import {
  IReportMeasurement,
  IReportMeasurementGroup,
  GetSymbolUnitSystem,
  IReportConfiguration,
  ReportKind,
} from 'Data';
import {
  IMultiShotControlParams,
  ISponsorDetailsProps,
  INormalizedCheckboxTextProps,
  IFooterWithCommercialProps,
  IShareButtonsProps,
  SponsorDetails,
  ReportWithNoData,
  FooterWithCommercial,
  HeaderWithCommercials,
  MultiShotControl,
  ShareButtons,
  StrokesGroupButton,
  IHeaderWithCommercialProps,
  ImageHandler,
  NormalizedCheckboxText,
} from 'Components';
import { autorun, action } from 'mobx';
import { observer } from 'mobx-react';
import { MultiShotHelpers } from './../../Reports/ReportsExportedFiles';
import { DrShotAnalysisReportStore } from 'Reports';
import { tps } from '../../Tps/TpsCallback';
import * as IDrShotAnalysisReportProps from './IDrShotAnalysisReportProps';
import { NormalizationSettingsType } from 'Components/NormalizationSettings/configs';
import { ReportComponentState } from 'Reports/Helpers/models';

@observer
export class DrShotAnalysisReport extends React.Component<
  IDrShotAnalysisReportProps.IDrShotAnalysisReportProps,
  ReportComponentState
> /*BaseReport*/ {
  private reportDataService: IReportDataService = Injector.resolve<IReportDataService>(
    'IReportDataService'
  );
  private dataConversionService: IReportDataConversionService = Injector.resolve<
    IReportDataConversionService
  >('IReportDataConversionService');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private subscriptions: Array<any> = [];
  private reportStore: DrShotAnalysisReportStore;
  private skipOneTimeHistoryPush = false;

  constructor(props: IDrShotAnalysisReportProps.IDrShotAnalysisReportProps) {
    super(props, undefined);
    this.reportStore = props.store;
    this.state = {
      isUpdatingReport: false,
    };
    tps.reportConfiguration(this.reportStore);

    MultiShotHelpers.loadDataToState(
      this.reportDataService,
      this.reportStore,
      this.props.spinnerEventHandler
    ).done(
      action(() => {
        //Url state has higher priority, so we overwrite.
        MultiShotHelpers.deserializeStateFromUrl(this.reportStore);
        this.subscriptions.push(
          autorun(() => {
            if (this.skipOneTimeHistoryPush) {
              this.skipOneTimeHistoryPush = false;
              return;
            }
            const queryString = '?' + MultiShotHelpers.serializeStateToUrlQuery(this.reportStore);
            const pathName = window.location.pathname;
            const newUrl = pathName + queryString;
            window.history.pushState(this.reportStore.toReportConfiguration(), '', newUrl);
            this.reportStore.reportPrintUrl = newUrl + '&p=1&rfu';
            this.reportStore.currentUrl = newUrl;
            if (this.reportStore.isWeb) {
              this.reportStore.shareLink = window.location.origin + newUrl + '&rfu';
            }
          })
        );
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        //move url's state managing to separate module
        window.addEventListener('popstate', (e: PopStateEvent) => {
          if (!e.state) {
            return;
          }
          if (!self) {
            console.error('no state');
          }
          const reportConfigs = e.state as IReportConfiguration;
          self.skipOneTimeHistoryPush = true;
          self.reportStore.fromReportConfiguration(reportConfigs);
          self.reportStore.currentUrl = window.location.pathname + window.location.search;
        });
      })
    );

    this.reportStore.showOptimizerTutorial = MultiShotHelpers.checkIfOptimizerTutorialShouldBeShown();
  }

  componentWillUnmount(): void {
    this.subscriptions.forEach(disposer => disposer());
  }

  componentWillReceiveProps() {
    this.setState({
      isUpdatingReport: false,
    });
  }

  @action.bound
  private loadTiles(): void {
    MultiShotHelpers.loadTiles(this.reportStore);
  }
  @action.bound
  private addTile(param: string, visibleCount: number): boolean {
    return MultiShotHelpers.addTile(param, visibleCount, this.reportStore);
  }
  @action.bound
  private displayStrokesGroup(index: number) {
    MultiShotHelpers.displayStrokesGroup(index, this.reportStore, tps);
  }
  @action.bound
  private disableTile(index: number) {
    this.reportStore.measurementParameters[index].isVisible = false;
  }
  @action.bound
  private hideSingleShot(stroke: IReportMeasurement, measurementGroup: IReportMeasurementGroup) {
    if (stroke.isVisible) {
      // Clearing selected stroke if visibility of stroke has changed
      if (this.reportStore.selectedStroke) {
        if (this.reportStore.selectedStroke.id === stroke.id) {
          this.cleanSelectedShot();
        }
      }
    }

    stroke.isVisible = !stroke.isVisible;
    const normalStrokes = measurementGroup.normalStrokes.filter(x => x.id === stroke.id);
    if (normalStrokes.length > 0) {
      normalStrokes[0].isVisible = stroke.isVisible;
    }

    const normalizedStrokes = measurementGroup.normalizedStrokes.filter(x => x.id === stroke.id);
    if (normalizedStrokes.length > 0) {
      normalizedStrokes[0].isVisible = stroke.isVisible;
    }

    if (this.reportStore.selectedAverage) {
      this.selectAverage(measurementGroup.strokes ?? [], measurementGroup);
    }
  }
  @action.bound
  private changeUnit(newValue: string) {
    const unitSystem = GetSymbolUnitSystem(newValue) || UnitSystem.Metric;
    this.reportStore.unitPresentation = new UnitPresentation(unitSystem);
    this.reportStore.selectedUnit = newValue;
  }

  @action.bound
  private cleanSelectedShot() {
    MultiShotHelpers.cleanSelectedShot(this.reportStore);
  }

  @action.bound
  private selectShot(stroke: IReportMeasurement, measurementGroup: IReportMeasurementGroup) {
    MultiShotHelpers.selectShot(stroke, measurementGroup, this.reportStore);
  }

  @action.bound
  private selectAverage(
    strokes: Array<IReportMeasurement>,
    measurementGroup: IReportMeasurementGroup
  ) {
    MultiShotHelpers.selectAverage(strokes, measurementGroup, this.reportStore);
  }

  @action.bound
  private collapseStrokesGroup(measurementGroup: IReportMeasurementGroup) {
    measurementGroup.isCollapsed = !measurementGroup.isCollapsed;
  }

  @action.bound
  private updateUnitOnTiles(): void {
    MultiShotHelpers.updateUnitOnTiles(this.reportStore);
  }

  @action.bound
  changeData() {
    this.reportStore.showNormalizedData = !this.reportStore.showNormalizedData;
  }

  @action.bound
  toggleNormalization(value: boolean) {
    this.reportStore.showNormalizedData = value;
    // this.setState({
    //   isUpdatingReport: true,
    // });
    // this.props.updateReportData?.();
    this.changeStrokesValues();
  }

  @action.bound
  setNormalizationSettings = (normalizationSettings: NormalizationSettingsType) => {
    this.reportStore.normalizationSettings = normalizationSettings;
    this.setState({
      isUpdatingReport: true,
    });
    this.props.updateReportData?.(normalizationSettings);
  };

  @action.bound
  changeStrokesValues() {
    MultiShotHelpers.changeStrokesAccordingToIsNormalized(this.reportStore);
  }
  @action.bound
  private onClickMailButton() {
    MultiShotHelpers.onClickMailButton(this.reportStore);
  }

  @action.bound
  private shareOnTwitterOnClick() {
    MultiShotHelpers.shareOnTwitterOnClick(this.reportStore);
  }

  @action.bound
  private shareOnFacebookOnClick() {
    MultiShotHelpers.shareOnFacebookOnClick(this.reportStore);
  }

  render() {
    const isDrShotAnalysisReport = this.reportStore.reportKind === ReportKind.drShotAnalysis;
    const isNormalizedCheckNotChecked = !this.reportStore.showNormalizedData
      ? 'glyphicon-unchecked'
      : 'glyphicon-check';
    return (
      this.reportStore.showReport && (
        <div className={DrShotAnalysisReportStyle.DrShotAnalysisReport}>
          {
            <div>
              {this.reportStore.isTps && (
                <div className="visible-print-block">
                  <HeaderWithCommercials {...this.getHeaderWithCommercialProps()} />
                </div>
              )}
              {this.reportStore.isWeb && (
                <div>
                  <HeaderWithCommercials {...this.getHeaderWithCommercialProps()} />
                </div>
              )}
              <div className="hidden-print">
                <div className={`${DrShotAnalysisReportStyle.individualPlayerShots} row`}>
                  {this.reportStore.individualPlayers.map((currentPlayer, i) => {
                    const strokeGroupsBtns = this.reportStore.measurementsData.map(
                      (measurementGroup, j) => {
                        if (measurementGroup.player?.id === currentPlayer.id) {
                          return (
                            <StrokesGroupButton
                              key={measurementGroup.id + j}
                              reportKind={this.reportStore.reportKind}
                              measurementGroup={measurementGroup}
                              groupIndex={j}
                              displayStrokesGroup={this.displayStrokesGroup}
                              unitPresentation={this.reportStore.unitPresentation}
                              selectedUnit={this.reportStore.selectedUnit}
                              className=""
                            />
                          );
                        }
                        return null;
                      }
                    );

                    if (currentPlayer) {
                      return [
                        <div
                          className={`${DrShotAnalysisReportStyle.playerInfo} col-xs-12`}
                          key={i}
                        >
                          <div className={DrShotAnalysisReportStyle.playerImg}>
                            {this.reportStore.isWeb && (
                              <img
                                src={ImageHandler.getImageUrl(currentPlayer.id)}
                                alt=""
                                onError={({ currentTarget }) => {
                                  currentTarget.onerror = null; // prevents looping
                                  currentTarget.src = 'Images/no-avatar.png';
                                }}
                              />
                            )}
                            {!this.reportStore.isWeb && currentPlayer.image && (
                              <img src={currentPlayer.image} alt="" />
                            )}
                            {!this.reportStore.isWeb && !currentPlayer.image && (
                              <img src={'Images/no-avatar.png'} alt="" />
                            )}
                          </div>
                          <span className={DrShotAnalysisReportStyle.playerName}>
                            {currentPlayer.name}
                          </span>
                        </div>,
                        strokeGroupsBtns,
                      ];
                    }
                    return null;
                  })}
                </div>
              </div>
              <div className="printed-first-page visible-print-block">
                <div className="report-printed-paragraph visible-print-block">
                  <h1>TrackMan Report</h1>
                </div>
                {this.reportStore.isWeb && (
                  <div className="visible-print-block sponsor-details">
                    <SponsorDetails {...this.getSponsorDetailsParameters()} />
                  </div>
                )}
              </div>
              {/* {this.reportStore.reportKind === ReportKind.drShotAnalysis && (
                <NormalizationSettings
                  disabled={this.state.isUpdatingReport}
                  isActive={this.reportStore.showNormalizedData}
                  setIsActive={this.toggleNormalization}
                  value={this.reportStore.normalizationSettings}
                  onChange={this.setNormalizationSettings}
                />
              )} */}
              <MultiShotControl {...this.getMultiShotControlParameters()} />
              {isDrShotAnalysisReport && (
                <div className="normalized-data-check">
                  <div
                    className={`glyphicon checkbox-icon glyphicon-check ${isNormalizedCheckNotChecked}`}
                    onClick={() => this.toggleNormalization(!this.reportStore.showNormalizedData)}
                  ></div>
                  <NormalizedCheckboxText {...this.getNormalizedCheckboxText()} />
                </div>
              )}
              {this.reportStore.isWeb && <ShareButtons {...this.getShareButtons()} />}
            </div>
          }
          {this.reportStore.noStrokes && <ReportWithNoData />}
          {<FooterWithCommercial {...this.getFooterWithCommercial()} />}
        </div>
      )
    );
  }
  private getHeaderWithCommercialProps() {
    const result: IHeaderWithCommercialProps = {
      individualPlayers: this.reportStore.individualPlayers,
      reportDate: this.reportStore.reportDate,
      sponsors: this.reportStore.sponsors,
      facilities: this.reportStore.facilities,
      reportKind: this.reportStore.reportKind,
      isWeb: this.reportStore.isWeb,
      isTps: this.reportStore.isTps,
      siteName: this.reportStore.siteName,
    };
    return result;
  }

  private getShareButtons() {
    const result: IShareButtonsProps = {
      shareLink: this.reportStore.shareLink,
      reportKind: this.reportStore.reportKind,
      setShareLink: (link: string) => (this.reportStore.shareLink = link),
      onClickMailButton: this.onClickMailButton,
      shareOnTwitterOnClick: this.shareOnTwitterOnClick,
      shareOnFacebookOnClick: this.shareOnFacebookOnClick,
      reportPrintUrl: this.reportStore.reportPrintUrl,
    };
    return result;
  }
  private getFooterWithCommercial() {
    const result: IFooterWithCommercialProps = {
      showBottomCommercial: this.reportStore.showBottomCommercial,
      editModeInTps: this.reportStore.editModeInTps,
      isTps: this.reportStore.isTps,
    };
    return result;
  }

  private getNormalizedCheckboxText() {
    const result: INormalizedCheckboxTextProps = {
      reportKind: this.reportStore.reportKind,
      showNormalizedData: this.reportStore.showNormalizedData,
      normalizedEnvironmentDetails: this.reportStore.normalizedEnvironmentDetails,
      unitPresentation: this.reportStore.unitPresentation,
    };
    return result;
  }
  private getSponsorDetailsParameters() {
    const result: ISponsorDetailsProps = {
      sponsors: this.reportStore.sponsors,
      facilities: this.reportStore.facilities,
      isWeb: this.reportStore.isWeb,
      isTps: this.reportStore.isTps,
    };
    return result;
  }

  private getMultiShotControlParameters() {
    const unitPresentation = this.reportStore.unitPresentation;
    const measurementParameters = this.reportStore.measurementParameters;
    const selectedGroupOfStrokes = this.reportStore.selectedGroupOfStrokes;
    const selectedAverage = this.reportStore.selectedAverage;

    const result: IMultiShotControlParams = {
      reportKind: this.reportStore.reportKind,
      measurementsData: this.reportStore.measurementsData,
      unitPresentation: unitPresentation,
      selectedStroke: this.reportStore.selectedStroke,
      selectedAverage: selectedAverage,
      selectedGroupOfStrokes: selectedGroupOfStrokes,
      selectedStrokesGroups: this.reportStore.selectedStrokesGroups,
      selectedClub: this.reportStore.clubType,
      tilesToDisplay: this.reportStore.tilesToDisplay,
      selectedView: this.reportStore.selectedView,
      showNormalizedData: this.reportStore.showNormalizedData,
      videos: this.reportStore.videos ?? [],
      individualPlayers: this.reportStore.individualPlayers,
      isTps: this.reportStore.isTps,
      isIos: this.reportStore.isIos,
      isWeb: this.reportStore.isWeb,
      multiShotReportDataService: this.reportDataService,
      measurementParameters: measurementParameters,
      sponsors: this.reportStore.sponsors,
      facilities: this.reportStore.facilities,
      cleanSelectedShot: this.cleanSelectedShot,
      selectShot: this.selectShot,
      disableTile: this.disableTile,
      hideSingleShot: this.hideSingleShot,
      selectAverage: this.selectAverage,
      collapseStrokesGroup: this.collapseStrokesGroup,
      loadTiles: this.loadTiles,
      updateUnitOnTiles: this.updateUnitOnTiles,
      addTile: this.addTile,
      changeUnit: this.changeUnit,
      displayStrokesGroup: this.displayStrokesGroup,
      selectedUnit: this.reportStore.selectedUnit,
      reportStore: this.reportStore,
    };
    return result;
  }
}
